import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

function Steps({ t }) {
  return (
    <ol className="list-decimal pl-5 text-sm text-slate-700 space-y-1" data-testid="chatgpt-steps">
      <li>{t("Open ChatGPT (web) → Settings → Apps & Connectors → Advanced → turn on")} <b>Developer mode</b>.</li>
      <li>{t("Click")} <b>Create</b>, {t("name it")} <b>Leprecon+ CRM</b>, {t("paste the connector URL below, choose")} <b>No authentication</b> {t("and")} <b>Scan tools</b>.</li>
      <li>{t("In a chat, enable the Leprecon+ CRM app next to Gmail. Ask e.g.")} <i>“{t("Read the e-mail from Juan and reply with his order status from the CRM")}”</i>.</li>
    </ol>
  );
}

function CallsLog({ calls, t }) {
  if (!calls?.length) return <div className="text-xs text-slate-400" data-testid="chatgpt-calls-empty">{t("No ChatGPT calls yet.")}</div>;
  return (
    <div className="space-y-1 max-h-48 overflow-auto" data-testid="chatgpt-calls">
      {calls.map((c, i) => (
        <div key={i} className="text-[11px] font-mono text-slate-600 flex gap-2" data-testid={`chatgpt-call-${i}`}>
          <span className="text-slate-400 shrink-0">{fmt(c.at)}</span>
          <span className={c.ok ? "text-emerald-700" : "text-red-700"}>{c.tool}</span>
          <span className="truncate">{Object.entries(c.args || {}).map(([k, v]) => `${k}=${v}`).join(" · ")}</span>
        </div>
      ))}
    </div>
  );
}

export function ChatGptConnectorCard() {
  const { t } = useT();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [reveal, setReveal] = useState(false);
  const { data: st } = useQuery({ queryKey: ["mcp-connector"], queryFn: async () => (await api.get("/mcp-connector/status")).data, refetchInterval: 30_000 });
  const save = useMutation({
    mutationFn: (patch) => api.put("/mcp-connector/settings", patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mcp-connector"] }); toast.success(t("Saved")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const rotate = useMutation({
    mutationFn: () => api.post("/mcp-connector/rotate"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mcp-connector"] }); toast.success(t("New connector URL created — update it in ChatGPT")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (!st) return null;
  const copy = async () => { try { await navigator.clipboard.writeText(st.url); toast.success(t("Connector URL copied")); } catch { toast.error(t("Copy failed — select the URL manually")); } };
  return (
    <div id="chatgpt" className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="chatgpt-connector-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xl">🔌</span>
        <h2 className="font-semibold text-lg">{t("ChatGPT Connector")}</h2>
        <Badge className={`border ${st.enabled ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`} data-testid="chatgpt-enabled-badge">{st.enabled ? t("ON") : t("OFF")}</Badge>
        {st.last_used_at && <Badge className="border border-sky-300 bg-sky-50 text-sky-800 text-[11px]" data-testid="chatgpt-last-used">{t("last call")} {fmt(st.last_used_at)}{st.last_client ? ` · ${st.last_client}` : ""}</Badge>}
        <div className="ml-auto flex items-center gap-2 text-sm">
          <Label htmlFor="chatgpt-enabled" className="text-xs">{t("Connector active")}</Label>
          <Switch id="chatgpt-enabled" checked={!!st.enabled} onCheckedChange={(v) => save.mutate({ enabled: v })} data-testid="chatgpt-enabled-switch" />
        </div>
      </div>
      <p className="text-sm text-slate-600">{t("Your own ChatGPT (already connected to your Gmail) can look up any order in this CRM — stage, dates, tracking, claims and team notes — and write a note back after it answers a customer. Nothing in the CRM reads your mailbox.")}</p>
      <Steps t={t} />
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="chatgpt-url-row">
        <Label className="text-xs font-semibold">{t("Connector URL")} <span className="text-slate-400 font-normal">· {t("keep it private — the URL is the key")}</span></Label>
        <div className="flex flex-wrap items-center gap-2">
          <code className="text-xs bg-white border border-slate-300 rounded px-2 py-1.5 break-all flex-1 min-w-[260px]" data-testid="chatgpt-url">{reveal ? st.url : st.url.replace(/\/api\/mcp\/.*$/, `/api/mcp/${st.token_hint}`)}</code>
          <Button size="sm" variant="outline" onClick={() => setReveal((v) => !v)} data-testid="chatgpt-url-reveal">{reveal ? t("Hide") : t("Reveal")}</Button>
          <Button size="sm" onClick={copy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="chatgpt-url-copy">📋 {t("Copy URL")}</Button>
          {user.role === "super_admin" && (
            <Button size="sm" variant="outline" onClick={() => { if (window.confirm(t("Create a new URL? The old one stops working and must be replaced in ChatGPT."))) rotate.mutate(); }} disabled={rotate.isPending} className="text-red-700 border-red-200" data-testid="chatgpt-url-rotate">↻ {t("New URL")}</Button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3" data-testid="chatgpt-write-row">
        <Switch checked={!!st.allow_write} onCheckedChange={(v) => save.mutate({ allow_write: v })} data-testid="chatgpt-write-switch" />
        <div className="text-sm"><b>{t("Let ChatGPT add notes on orders")}</b><div className="text-[11px] text-slate-500">{t("e.g. “Replied to customer about tracking” — appears in the order Notes tagged ChatGPT. Reading is always allowed while the connector is active.")}</div></div>
      </div>
      <div>
        <Label className="text-xs font-semibold">{t("What ChatGPT can do")}</Label>
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid="chatgpt-tools">
          {st.tools.map((tool) => <Badge key={tool.name} className="border border-slate-300 bg-white text-slate-700 font-mono text-[11px]" title={tool.description}>{tool.name}</Badge>)}
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold">{t("Recent ChatGPT calls")} <span className="text-slate-400 font-normal">· {st.calls_total} {t("total")}</span></Label>
        <div className="mt-1"><CallsLog calls={st.recent_calls} t={t} /></div>
      </div>
    </div>
  );
}
